/**
 * LegacyRedirect — `<Navigate replace>` for a retired route that KEEPS the
 * bookmark's intent (lifted from frontend/apps/ai/src/components/LegacyRedirect.tsx,
 * 2026-10-12, so every shell — and SettingsRedirect — gets the same behavior).
 *
 * A bare `<Navigate to="/x?tab=y" replace />` silently drops the incoming query
 * string and hash: a bookmarked `/planning/list?status=PARTIAL` landed on the
 * unfiltered list (ai P04 surface review, 2026-10-12). This merges the incoming
 * params into the target:
 *
 *   - params the target URL sets (e.g. `tab=docs`) WIN — they encode where the
 *     retired page now lives;
 *   - every other incoming param (filters, page, ids) is carried over;
 *   - the incoming `#hash` is kept unless the target sets its own.
 *
 * Always `replace`, so the legacy URL never becomes a back-button trap.
 *
 * @layer routing
 * @package @plerity/dashboard-shell
 */

import { Navigate, useLocation } from "react-router-dom";

/** Pure merge of a legacy URL's search/hash into a redirect target. */
export function legacyRedirectTarget(to: string, search = "", hash = ""): string {
  const hashIdx = to.indexOf("#");
  const toHash = hashIdx >= 0 ? to.slice(hashIdx) : "";
  const toNoHash = hashIdx >= 0 ? to.slice(0, hashIdx) : to;
  const qIdx = toNoHash.indexOf("?");
  const pathname = qIdx >= 0 ? toNoHash.slice(0, qIdx) : toNoHash;
  const targetParams = new URLSearchParams(qIdx >= 0 ? toNoHash.slice(qIdx + 1) : "");

  const merged = new URLSearchParams(search);
  for (const key of new Set(targetParams.keys())) {
    merged.delete(key);
    for (const value of targetParams.getAll(key)) merged.append(key, value);
  }
  const qs = merged.toString();
  const finalHash = toHash || (hash && hash !== "#" ? hash : "");
  return `${pathname}${qs ? `?${qs}` : ""}${finalHash}`;
}

export function LegacyRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={legacyRedirectTarget(to, search, hash)} replace />;
}

export default LegacyRedirect;
